import { useEffect } from 'react'
import { throttle, debounce } from 'lodash-es'
import { breakPoints } from '~/styles/breakpoints'

class ScrollableSlideShow {
  container: HTMLElement
  wrapper: HTMLDivElement | null
  items: HTMLDivElement[]
  offsetY = 0
  currentStep: number | null = null
  stepsBorders: Array<[number, number]> = []
  started = false

  constructor(containerRef: HTMLElement) {
    this.container = containerRef
    this.wrapper = this.container.querySelector('div')
    this.items = this.wrapper ? Array.from(this.wrapper.querySelectorAll(':scope > div')) : []

    if (!this.items.length) return

    this.init()
  }

  resetItems = () => {
    this.items.forEach((item, i) => {
      if (i === 0) {
        item.classList.add('show', 'static')
      } else {
        item.classList.remove('show')
      }
    })
  }

  showStep = throttle((stepNumber) => {
    if (stepNumber === this.currentStep) return false

    if (this.currentStep !== null) this.items[this.currentStep].classList.remove('show')
    this.items[stepNumber].classList.add('show')
    this.currentStep = stepNumber
  }, 350)

  setSizes = () => {
    if (!this.wrapper) return

    this.offsetY = parseInt(window.getComputedStyle(this.wrapper).top) || 0
    const { height: trackHeight, top } = this.container.getBoundingClientRect()
    const trackTopPos = top + window.scrollY
    const itemsLength = this.items.length
    const lastItemHeight = this.items[itemsLength - 1].getBoundingClientRect().height

    const stepHeight = (trackHeight - lastItemHeight) / itemsLength
    this.stepsBorders = this.items.map((_, i) => {
      const nextIndex = i + 1
      const top = i === 0 ? 0 : stepHeight * i + trackTopPos
      const bottom = nextIndex < itemsLength ? stepHeight * nextIndex + trackTopPos : Infinity

      return [top, bottom]
    })

    this.wrapper.style.height = `${lastItemHeight}px`
  }

  handleResize = debounce(() => {
    if (window.innerWidth <= breakPoints.Tablet) {
      this.stop()
    } else {
      this.start()
      this.setSizes()
      this.handleScroll()
    }
  }, 300)

  handleScroll = () => {
    const scrollTop = window.scrollY + this.offsetY

    this.stepsBorders.some((step, index) => {
      const [topBorderStep, bottomBorderStep] = step

      if (scrollTop >= topBorderStep && scrollTop < bottomBorderStep) {
        this.showStep(index)
        return true
      }
      return false
    })
  }

  init = () => {
    if (this.items.length > 1) {
      this.start()
      window.addEventListener('resize', this.handleResize)
      this.handleResize()
    } else {
      this.resetItems()
    }
  }

  start = () => {
    if (this.started || this.items.length <= 1) return

    window.addEventListener('scroll', this.handleScroll)
    this.started = true
  }

  stop = () => {
    if (!this.started) return

    window.removeEventListener('scroll', this.handleScroll)
    if (this.wrapper) this.wrapper.style.height = ''
    this.currentStep = 0
    this.resetItems()
    this.started = false
  }

  destroy = () => {
    this.stop()
    window.removeEventListener('resize', this.handleResize)
  }
}

export const useSlideAside = (containerRef: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!containerRef.current) return
    const scrollableSlideShow = new ScrollableSlideShow(containerRef.current)

    return scrollableSlideShow.destroy
  }, [containerRef])
}
